import * as React from 'react';
import { Overlay, css } from 'office-ui-fabric-react';
import { Dictionary, EntityType } from '../../../entities/common';
import PostMessageReceiver from '../../integration/PostMessageReceiver';
import Spinner from '../Spinner';
import './EmbeddedContent.css'
import { ApplicationState } from '../../../store';
import { connect } from 'react-redux';
import { post } from '../../../fetch-interceptor';
import { error } from 'console';

type MessageData = {
    id: string,
    type: string,
    version: string,
    data?: any,
    error?: string;
}

enum PPMXMessageType {
    RequestConnection = "ppmx_extension_request_connection",
    ResponseConnection = "ppmx_extension_response_connection",
    RequestContext = "ppmx_extension_request_context",
    ResponseContext = "ppmx_extension_response_context",
    RequestAccessToken = "ppmx_extension_request_access_token",
    ResponseAccessToken = "ppmx_extension_response_access_token",
}

type OwnProps = {
    style?: React.CSSProperties,
    context?: Dictionary<any>
}

type StateProps = {
    tenantId: string;
    userId: string;
}

type Props = OwnProps & StateProps;

const PARENT_ENTITY_ID = "parentEntityId";
const ENTITY_ID = "entityId";
const ENTITY_TYPE = "entityType";
const SOURCE_TYPE = "sourceType";
const TENANT_ID = "tenantId";
const USER_ID = "userId";

const allowedContext = [PARENT_ENTITY_ID, ENTITY_ID, ENTITY_TYPE, SOURCE_TYPE, TENANT_ID, USER_ID];

export const tryGetUrl = (url: string | URL | undefined, context: Dictionary<string>): { success: boolean, value: URL | undefined } => {
    if (!url) {
        console.error("Url is empty");
        return { success: false, value: undefined };
    }

    const data = allowedContext.reduce<Dictionary<string>>((acc, key) => {
        if (key in context) {
            acc[key] = context[key];
        }
        return acc;
    }, {});

    const urlTemplate = url instanceof URL ? decodeURI(url.toString()) : url;
    
    // the same is in ExtensionsService.cs on server side
    const urlString = urlTemplate.replace(/{(.*?)}/g, (match, key) => data[key] ?? match);

    try {
        const urlObj = new URL(urlString);
        return { success: true, value: urlObj };
    } catch (err) {
        console.error("Invalid url");
        return { success: false, value: undefined }
    }
}

const EmbeddedContent = (props: Props & { url: string | URL | undefined }) => {
    const { url, style, tenantId, userId } = props;
    const context = {...(props.context || {}), tenantId, userId };
    const result = tryGetUrl(url, context);
    return result.success
        ? <ExternalContent url={result.value!} context={context} style={style} />
        : null;
}

const ExternalContent = (props: { url: URL, context: Dictionary<any>, style?: React.CSSProperties }) => {
    const version = "1.0.0";
    const { url, style, context } = props;

    const [isLoading, setIsLoading] = React.useState(true);
    const channel = React.useRef<MessageChannel>();
    const closeChannel = () => {
        if (channel.current) {
            channel.current.port1.close();
            channel.current.port2.close();
            channel.current = undefined;
        }
    }

    React.useEffect(() => () => closeChannel(), []);

    const recreateChannel = () => {
        closeChannel();
        channel.current = new MessageChannel();
        channel.current.port2.onmessage = onMessage;
    }

    const _iframe = React.useRef<HTMLIFrameElement>(null);

    const setupClientPort = (e: MessageEvent, data: MessageData) => {
        if (data?.type !== PPMXMessageType.RequestConnection) {
            return;
        }
        setIsLoading(false);
        recreateChannel();
        const message: MessageData = {
            type: PPMXMessageType.ResponseConnection,
            id: data.id,
            version: version,
        }
        try {
            if (channel.current) {
                _iframe.current?.contentWindow?.postMessage(message, url.origin, [channel.current.port1]);
            }
        }
        catch (error) {
            console.error(error);
        }
    }

    const onMessage = (ev: MessageEvent<MessageData>) => {
        if (ev.data?.type === PPMXMessageType.RequestContext) { 
            sendMessage({
                id: ev.data.id,
                type: PPMXMessageType.ResponseContext,
                version: version,
                data: context
            });
            return;
        }
        if (ev.data?.type === PPMXMessageType.RequestAccessToken) { 
            const message = {
                id: ev.data.id,
                type: PPMXMessageType.ResponseAccessToken,
                version: version
            };
            post<string>('api/user/pat/extension', {})
                .then(token => sendMessage({...message, data: token}))
                .catch(_ => sendMessage({...message, data: {error: _}}));
            return;
        }
    };

    const sendMessage = (message?: MessageData) => {
        if(!message){
            return;
        }
        try {
            channel.current?.port2.postMessage(message);
        }
        catch (error) {
            console.error(error);
        }
    }    

    const validateMessage = (e: MessageEvent) => {
        return e.origin === url.origin;
    }

    return <div className="embedded-content" style={style}>
        <PostMessageReceiver<MessageData> validateMessage={validateMessage} onMessageReceived={setupClientPort} />
        <iframe ref={_iframe}
            src={url.href}
            allow="clipboard-write"
            role="presentation"
            frameBorder="0"
            className={css(isLoading && "hidden")}
            onLoad={() => setIsLoading(false)} />
        {isLoading && <Overlay><Spinner /></Overlay>}
    </div>
}

export const buildEntityContext = (entityType: EntityType, entityId: string): { entityType: EntityType, entityId: string } => ({ entityType, entityId })

function mapStateToProps(state: ApplicationState, ownProps: OwnProps): StateProps {
    return {
        tenantId: state.tenant.id,
        userId: state.user.id!
    };
}
export default connect(mapStateToProps)(EmbeddedContent);